import { getFormattedDateShortMonthDDYYYY, Link, useReviews } from '@marageti/z4-lib';
import React, { useState } from 'react';
import {
  Avatar, Box, Card, CardContent, Divider, Rating, Stack, Typography,
} from '@mui/material';
import UseInfiniteScroll from 'react-infinite-scroll-hook';
import useTotalReviews from '../../../hooks/use-total-reviews';
import ZSpinner from '../../../components/z-lib/z-spinner';

interface ReviewItemProps {
  review: {
    id: string;
    title: string;
    rating: number;
    travelerName: string;
    travelerAvatar?: string;
    createdAt: string;
  };
}

const ReviewItem = ({ review }: ReviewItemProps) => {
  const {
    title,
    rating,
    travelerName,
    travelerAvatar,
    createdAt,
  } = review;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'grey.100',
        },
      }}
    >
      <Stack spacing={1}>
        <Rating value={rating} size="small" readOnly />
        <Typography variant="bodyBold" component="div">
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {travelerAvatar && (
            <Avatar
              src={travelerAvatar}
              alt={travelerName}
              sx={{ height: 16, width: 16 }}
            />
          )}
          <Typography variant="caption">
            {`${travelerName} • ${getFormattedDateShortMonthDDYYYY(createdAt)}`}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

interface ReviewCardProps {
  partnerId: string;
}

const ReviewCard = ({ partnerId }: ReviewCardProps) => {
  const [showAll, setShowAll] = useState(false);

  const {
    reviews,
    loading: loadingReviews,
    error: errorReviews,
    loadMore: loadMoreReviews,
    hasNextPage,
  } = useReviews({
    searchItemFieldName: 'partnerId',
    searchItemId: partnerId,
  });

  const {
    totalReviews,
    loadingTotal,
    errorTotal,
  } = useTotalReviews({
    searchItemId: partnerId,
    searchItemFieldName: 'partnerId',
  });

  const [sentryRef] = UseInfiniteScroll({
    disabled: !!errorReviews,
    loading: loadingReviews,
    hasNextPage,
    onLoadMore: loadMoreReviews,
  });

  const isLoading = loadingReviews && !reviews;
  const error = errorReviews || errorTotal;

  const displayedReviews = showAll ? reviews : reviews?.slice(0, 3);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setShowAll(true);
    }
  };

  if ((!reviews && isLoading) || loadingTotal) {
    return <ZSpinner />;
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error" variant="body1">
            {`Error loading reviews: ${error.message}`}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!reviews?.length) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1">No reviews available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2} sx={{ pb: { desktop: 3, mobile: 4 } }}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="captionBold" component="div">
              Reviews (
              {totalReviews}
              {' '}
              Total)
            </Typography>

            {displayedReviews?.map((review, index) => (
              <React.Fragment key={review.id}>
                <Link
                  to={`/reviews/${review.id}`}
                  state={{ review }}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <ReviewItem review={review} />
                </Link>
                {index < displayedReviews.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            {/* If not showing all reviews and there are more than three, show the "See All" text */}
            {!showAll && reviews.length > 3 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Typography
                onClick={() => setShowAll(true)}
                onKeyDown={handleKeyDown}
                sx={{
                  cursor: 'pointer',
                  color: 'black',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  userSelect: 'none',
                  '&:focus': {
                    outline: '2px solid #000',
                    outlineOffset: '2px',
                  },
                }}
                variant="bodyBold"
                role="button"
                tabIndex={0}
                aria-pressed="false"
              >
                See All
              </Typography>
            </Box>
            )}

            {/* If showing all reviews, include the infinite scroll sentinel */}
            {showAll && (
            <Box display="flex" ref={sentryRef} mt={2} justifyContent="center">
              {hasNextPage && <ZSpinner />}
            </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ReviewCard;
